/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Tek kaynak `qubepro-core` reposudur; uydu projelere sync ile yayılır.
 */

import React from 'react';
import type { ModalProps } from './Modal';
import { FormModalSurface } from './FormModalSurface';
import {
    EntityAuditHistoryModal,
    type EntityAuditHistoryModalProps,
} from './EntityAuditHistoryModal';
import { Icon } from './ComponentIcon';
import { useAuth } from '../../context/AuthContext';
import './FormModal.css';

export interface FormModalAuditHistoryConfig
    extends Omit<EntityAuditHistoryModalProps, 'isOpen' | 'onClose'> {
    /**
     * Sayfa daha dar bir entity yetkisi uyguluyorsa açıkça verir. Verilmezse
     * FormModal merkezi `activityLog:read` yetkisini uygular; formun kendisine
     * erişim entity okuma sınırını zaten kurmuştur.
     */
    canRead?: boolean;
}

export interface FormModalProps {
    isOpen?: boolean;
    title: React.ReactNode;
    headerActions?: React.ReactNode;
    /**
     * Kaydedilmiş entity için kanonik form geçmişi.
     * Yeni kayıtta entityId olmadığı için düğme otomatik gizlenir.
     */
    auditHistory?: FormModalAuditHistoryConfig;
    /** Nested editor draft; persistence and history belong to the parent save. */
    auditHistoryScope?: { kind: 'embedded-draft'; reason: string };
    onClose: () => void;
    closeAriaLabel?: string;
    children: React.ReactNode;
    bodyHeader?: React.ReactNode;
    contentClassName?: string;
    bodyLayout?: ModalProps['bodyLayout'];
    surfaceScope?: ModalProps['surfaceScope'];
    overlayClassName?: string;
    closeOnOverlayClick?: boolean;
    /** Overlay tıklamasıyla kapanmadan hemen önce çalışır. */
    onOverlayClick?: () => void;
    /** @deprecated Modal her zaman role="dialog" ve aria-modal kullanır. */
    overlayRole?: 'presentation';
    size?: ModalProps['size'];
    maximizable?: boolean;
    /** Keep a nested normal surface within its opener's parent form. */
    constrainToParent?: boolean;
    footer?: React.ReactNode;
}

/** FormBuilder ve kurumsal veri giriş ekranları için kanonik modal kabuğu. */
export const FormModal: React.FC<FormModalProps> = ({
    isOpen = true,
    title,
    headerActions,
    auditHistory,
    onClose,
    closeAriaLabel,
    children,
    bodyHeader,
    contentClassName,
    bodyLayout,
    surfaceScope,
    overlayClassName,
    closeOnOverlayClick = false,
    onOverlayClick,
    size = 'md',
    maximizable = true,
    constrainToParent = false,
    footer,
}) => {
    const { user } = useAuth();
    const [isAuditHistoryOpen, setIsAuditHistoryOpen] = React.useState(false);
    const auditEntityId = auditHistory?.entityId == null ? '' : String(auditHistory.entityId).trim();
    const hasCentralAuditPermission = Boolean(
        user
        && (
            user.isAdmin
            || user.permissions?.includes('*')
            || user.permissions?.includes('activityLog:read')
        ),
    );
    const canOpenAuditHistory = Boolean(
        isOpen
        && (auditHistory?.canRead ?? hasCentralAuditPermission)
        && auditEntityId,
    );
    const historyLanguage = auditHistory?.language ?? 'tr';

    React.useEffect(() => {
        if (!canOpenAuditHistory) setIsAuditHistoryOpen(false);
    }, [canOpenAuditHistory, auditEntityId]);

    return (
        <>
            <FormModalSurface
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                headerActions={
                    <>
                        {canOpenAuditHistory && (
                            <button
                                type="button"
                                className="modal-header-btn entity-audit-history-trigger"
                                onClick={() => setIsAuditHistoryOpen(true)}
                                aria-label={historyLanguage === 'tr' ? 'Kayıt geçmişini aç' : 'Open record history'}
                                title={historyLanguage === 'tr' ? 'Kayıt Geçmişi' : 'Record History'}
                            >
                                <Icon name="schedule" size={17} />
                            </button>
                        )}
                        {headerActions}
                    </>
                }
                size={size}
                maximizable={maximizable}
                constrainToParent={constrainToParent}
                platformAdaptiveControls
                closeAriaLabel={closeAriaLabel}
                contentClassName={contentClassName}
                bodyLayout={bodyLayout}
                surfaceScope={surfaceScope}
                overlayClassName={overlayClassName}
                closeOnOverlayClick={closeOnOverlayClick}
                onOverlayClick={onOverlayClick}
                footer={footer}
                bodyHeader={bodyHeader}
            >
                {children}
            </FormModalSurface>

            {auditHistory && (
                <EntityAuditHistoryModal
                    {...auditHistory}
                    isOpen={isAuditHistoryOpen && canOpenAuditHistory}
                    onClose={() => setIsAuditHistoryOpen(false)}
                />
            )}
        </>
    );
};

FormModal.displayName = 'FormModal';
